import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'app/core/auth/auth.service';
import {
    PROFILE_ACCESS_STATUSES,
    ProfileStatus,
    RoleList,
} from 'app/core/enums';
import { User } from 'app/core/models/filmmaker-user.model';
import { UserService } from 'app/shared/services/user.service';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import {
    ProfileCompletionGateComponent,
    ProfileCompletionGateData,
} from './profile-completion-gate.component';

/**
 * ProfileCompletionGateService
 *
 * Central service for checking profile status and showing the
 * "Complete Your Profile" or "Corrections Required" gate dialog.
 *
 * Access is granted when invitation.profileStatus is any of:
 *   SUBMITTED | IN_REVIEW | APPROVED   (see PROFILE_ACCESS_STATUSES)
 *
 * Error state blocks access but shows a different dialog variant
 * directing the user to the error resolution page.
 *
 * Non-filmmaker roles are never gated.
 * Tenants without invite/onboarding (no invitation object) are never gated.
 */
@Injectable({ providedIn: 'root' })
export class ProfileCompletionGateService {
    private readonly _auth = inject(AuthService);
    private readonly _dialog = inject(MatDialog);
    private readonly _userService = inject(UserService);

    /**
     * Returns true when the filmmaker can access all features.
     */
    isApproved(): boolean {
        const role = this._auth.currentUserRoleSubject?.value;
        if (role !== RoleList.FilmMaker) return true;

        const user = this._auth.userSubject?.value as User | null;
        const invitation = (
            user as unknown as {
                invitation?: { profileStatus?: string };
            }
        )?.invitation;

        // No invitation → non-invite-only tenant → always allow
        if (!invitation) return true;

        const invitationStatus = invitation.profileStatus ?? '';
        const topLevelStatus = user?.profileStatus ?? '';

        const result =
            PROFILE_ACCESS_STATUSES.has(invitationStatus) ||
            PROFILE_ACCESS_STATUSES.has(topLevelStatus);

        return result;
    }

    /**
     * Returns true when the user's profile is in Error state.
     */
    isErrorState(): boolean {
        const user = this._auth.userSubject?.value as User | null;
        return user?.profileStatus === ProfileStatus.ERROR;
    }

    /**
     * Show the profile gate dialog.
     * For Error state: fetches profile to get admin feedback, then shows dialog.
     * For Incomplete state: shows "Complete Profile" prompt immediately.
     */
    showGate(featureName?: string): Observable<boolean> {
        const isError = this.isErrorState();

        if (!isError) {
            // Incomplete state — show immediately, no fetch needed
            return this._openDialog({
                featureName,
                isErrorState: false,
            });
        }

        // Error state — fetch profile to get rejection message + fields
        return this._userService.refreshProfile().pipe(
            switchMap((profile) => {
                return this._openDialog({
                    featureName,
                    isErrorState: true,
                    errorMessage: profile.profileRejectionMessage || '',
                    errorFields: profile.profileRejectionFields || [],
                });
            }),
            catchError(() => {
                // If profile fetch fails, still show dialog without detailed message
                return this._openDialog({
                    featureName,
                    isErrorState: true,
                    errorMessage: '',
                    errorFields: [],
                });
            })
        );
    }

    /**
     * Guard helper — returns Observable<true> if approved, else shows gate
     * dialog and returns Observable<false>.
     */
    guardRoute(featureName?: string): Observable<boolean> {
        if (this.isApproved()) return of(true);
        return this.showGate(featureName).pipe(map(() => false));
    }

    private _openDialog(data: ProfileCompletionGateData): Observable<boolean> {
        const ref = this._dialog.open(ProfileCompletionGateComponent, {
            data,
            width: '440px',
            maxWidth: '90vw',
            disableClose: false,
        });
        return ref.afterClosed().pipe(map((result) => !!result));
    }
}
